/* =========================================================================
   TelemetryProvider

   Responsibilities:
   1. Resolve scenario deployment + active trueTwinId
   2. Hydrate dashboard/catalog configuration
   3. Hydrate historical telemetry
   4. Maintain a long-lived WebSocket for the active trueTwinId
   5. Merge live WS data with REST baseline data
   6. Prevent stale async requests / stale sockets from updating state

   WebSocket lifecycle:
   - Keep the socket alive while the provider remains mounted and the
     trueTwinId does not change.
   - Disconnect when:
       a) provider unmounts / user leaves the page
       b) active trueTwinId changes
   - Unexpected backend/network close -> reconnect for the same trueTwinId.
   ========================================================================= */

import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { v4 as uuidv4 } from "uuid";

import useTelemetry from "../components/center/telemetry/hooks/useTelemetry";
import { getEnvVar } from "../../../envUtils";
import useScenario from "../hooks/useScenario";
import { CommandCenterStateProviderContext } from "../context";
import {
  SCENARIO_DEPLOYMENT_TYPES,
  SCENARIO_STATUS,
} from "../../simulate/constants";

import {
  updateGlobalTelemetrySeriesStore,
  mergeAndSortTelemetryData,
  getTelemetryStorageKey,
  areWorkspacesEqual,
} from "../components/center/telemetry/lib/telemetry-helpers";

import {
  updateTelemetryCatalogFromApi,
  calculateOptimalInitialTimeframe,
} from "../../CommonComponents/telemetry";

import {
  normalizeWsPayload,
  mergeWsIncomingSeries,
} from "../components/center/telemetry/lib/telemetry-stream-helpers";

import { TelemetrySeries } from "../components/center/telemetry/lib/telemetry-types";

import TelemetryService from "../../../service/TelemetryService";
import SimulateService from "../../../service/SimulateService";

import useUrlParams from "../hooks/useUrlParams";
import useScenarioDeploymentDetails from "../hooks/useScenarioDeploymentDetails";
import { ScenarioDeploymentDetails } from "../types";

// =========================================================================
// Types
// =========================================================================

type TelemetryFetchParams = {
  startTime?: number;
  endTime?: number;
};

interface ScenarioDeploymentResult {
  deployDetails: ScenarioDeploymentDetails | null;
  activeTrueTwinId: string | null;
  telemetryFetchParams?: TelemetryFetchParams;
  initialTimeframeMinutes?: number;
}

interface DashboardAndCatalogResult {
  isDashOk: boolean;
  uniqueId: string;
  dashboardConfig: any;
  savedDashboardConfig: any;
  emptyDashboard: any;
  hasUnsavedChanges: boolean;
  initialActiveWorkspaceId: string | null;
}

interface WebSocketState {
  socket: WebSocket | null;
  trueTwinId: string | null;
  generation: number;
}

// =========================================================================
// Constants
// =========================================================================

const WS_BATCH_DELAY_MS = 200;
const WS_RECONNECT_DELAY_MS = 1000;
const WS_MERGE_LIMIT = 5000;

// =========================================================================
// Helpers
// =========================================================================

const isSimulationCompleted = (status?: string) =>
  status === SCENARIO_STATUS.SIMULATION_COMPLETED ||
  status === "SimulationCompleted";

const normalizeUnixSeconds = (value?: number | null) => {
  if (value == null || value <= 0) {
    return undefined;
  }

  return Math.floor(value > 2e9 ? value / 1000 : value);
};

// =========================================================================
// Stage 0
// Fetch deployment information and resolve active trueTwinId
// =========================================================================

async function fetchScenarioDeployment(
  missionId: string,
  scenarioId: string,
  selectedSatId?: string,
  fallbackScenario?: any
): Promise<ScenarioDeploymentResult> {
  let deployDetails: ScenarioDeploymentDetails | null = null;

  try {
    const deployRes = await SimulateService.getAllScenarioDeploymentInfo(
      missionId,
      scenarioId
    );

    const rawDeploy = deployRes?.data ?? deployRes;

    if (rawDeploy && typeof rawDeploy === "object") {
      deployDetails = rawDeploy;
    }
  } catch (error) {
    console.warn(
      "Failed to fetch scenario deployment info:",
      error
    );
  }

  const selectedSatellite = deployDetails?.satellites?.find(
    (sat) => sat.satelliteId === selectedSatId
  );

  const activeTrueTwinId =
    selectedSatellite?.trueTwinId ??
    deployDetails?.satellites?.[0]?.trueTwinId ??
    null;

  if (!activeTrueTwinId) {
    return {
      deployDetails,
      activeTrueTwinId: null,
    };
  }

  const simulationStartTime =
    deployDetails?.simulationStartTime ??
    fallbackScenario?.simulationStartTime;

  const simulationDurationMinutes =
    deployDetails?.simulationDuration ??
    fallbackScenario?.simulationDuration ??
    0;

  const simulationStartSec = normalizeUnixSeconds(simulationStartTime);

  const simulationEndSec =
    simulationStartSec != null
      ? simulationStartSec + simulationDurationMinutes * 60
      : undefined;

  const rawSimulationTime =
    selectedSatellite?.simulationTimeAbs ??
    deployDetails?.satellites?.[0]?.simulationTimeAbs;

  const simulationTimeAbsSec = normalizeUnixSeconds(rawSimulationTime);

  const endSec =
    simulationTimeAbsSec ??
    simulationEndSec;

  let telemetryFetchParams: TelemetryFetchParams | undefined;
  let initialTimeframeMinutes = 60;

  if (endSec != null) {
    initialTimeframeMinutes = calculateOptimalInitialTimeframe(
      simulationStartSec,
      endSec
    );

    const targetStartSec = endSec - initialTimeframeMinutes * 60;

    const startSec = simulationStartSec != null
      ? Math.max(simulationStartSec, targetStartSec)
      : targetStartSec;

    if (startSec < endSec) {
      telemetryFetchParams = {
        startTime: startSec,
        endTime: endSec,
      };
    }
  }

  return {
    deployDetails,
    activeTrueTwinId,
    telemetryFetchParams,
    initialTimeframeMinutes,
  };
}

// =========================================================================
// Stage 1
// Dashboard + telemetry catalog
// =========================================================================

async function fetchDashboardAndCatalog(
  trueTwinId: string,
  deploymentType?: string
): Promise<DashboardAndCatalogResult> {
  const dashboardTypeParam =
    deploymentType === SCENARIO_DEPLOYMENT_TYPES.OPERATE
      ? "operate"
      : "simulate";

  const [dashboardRes, parametersRes] =
    await Promise.allSettled([
      TelemetryService.getDashboardConfig(trueTwinId),
      TelemetryService.getTelemetryParameters(
        trueTwinId,
        dashboardTypeParam
      ),
    ]);

  const dashData =
    dashboardRes.status === "fulfilled"
      ? dashboardRes.value?.data ?? dashboardRes.value
      : null;

  const rawWorkspaces = Array.isArray(dashData?.workspaces)
    ? dashData.workspaces
    : [];

  const isDashOk = rawWorkspaces.length > 0;

  const uniqueId = dashData?.uniqueId ?? "telemetry-config";

  const emptyDashboard = {
    uniqueId: "config",
    truetwinId: trueTwinId,
    dashboardType: "SIMULATE" as const,
    workspaces: [],
  };

  if (!isDashOk) {
    return {
      isDashOk: false,
      uniqueId,
      dashboardConfig: emptyDashboard,
      savedDashboardConfig: emptyDashboard,
      emptyDashboard,
      hasUnsavedChanges: false,
      initialActiveWorkspaceId: null,
    };
  }

  let finalWorkspaces = rawWorkspaces;

  const storageKey = getTelemetryStorageKey(
    uniqueId,
    trueTwinId
  );

  try {
    const stored = sessionStorage.getItem(storageKey);

    if (stored) {
      const parsed = JSON.parse(stored);

      if (Array.isArray(parsed) && parsed.length > 0) {
        finalWorkspaces = parsed;
      }
    }
  } catch (error) {
    console.warn(
      "Failed to parse session telemetry workspaces:",
      error
    );
  }

  // Hydrate catalog parameters if available.
  if (parametersRes.status === "fulfilled" && parametersRes.value) {
    const paramData = parametersRes.value?.data ?? parametersRes.value;

    if (paramData && typeof paramData === "object") {
      updateTelemetryCatalogFromApi(paramData);
    }
  }

  const initialActiveWorkspaceId = finalWorkspaces[0]?.id ?? null;

  const hasUnsavedChanges = !areWorkspacesEqual(
    finalWorkspaces,
    rawWorkspaces
  );

  const dashboardType = dashData?.dashboardType ?? "SIMULATE";

  return {
    isDashOk: true,
    uniqueId,

    dashboardConfig: {
      uniqueId,
      truetwinId: trueTwinId,
      dashboardType,
      workspaces: finalWorkspaces,
    },

    savedDashboardConfig: {
      uniqueId,
      truetwinId: trueTwinId,
      dashboardType,
      workspaces: rawWorkspaces,
    },

    emptyDashboard,
    hasUnsavedChanges,
    initialActiveWorkspaceId,
  };
}

// =========================================================================
// Stage 2
// REST baseline + WS preload buffer
// =========================================================================

async function fetchBaselineTelemetryData(
  trueTwinId: string,
  fetchParams?: TelemetryFetchParams
): Promise<TelemetrySeries[]> {
  try {
    const raw = await TelemetryService.getAllPanelsTelemetryData(
      trueTwinId,
      fetchParams
    );

    let series: TelemetrySeries[] = [];

    if (Array.isArray(raw)) {
      series = raw;
    } else if (raw && typeof raw === "object") {
      if (
        (raw as any).id &&
        Array.isArray((raw as any).data)
      ) {
        series = [raw as TelemetrySeries];
      } else if (
        Array.isArray((raw as any).data)
      ) {
        series = (raw as any).data;
      }
    }

    return series;
  } catch (error) {
    console.error("Telemetry baseline data load error:", error);
    return [];
  }
}

// =========================================================================
// Provider
// =========================================================================

interface TelemetryProviderProps {
  children: ReactNode;
}

const TelemetryProvider = ({
  children,
}: TelemetryProviderProps) => {
  const {
    telemetrySeries,
    setTelemetrySeries,
    setTelemetryStatus,
    setTelemetryDashboards,
    setActiveTelemetryDashboardId,
    setHasUnsavedChanges,
    isTelemetryListening,
    setIsTelemetryDataLoading,
  } = useTelemetry();

  const {
    setScenarioDeploymentDetails,
  } = useScenarioDeploymentDetails();

  const {
    mission_id,
    scenario_id,
    sat_id,
  } = useUrlParams();

  const { scenario } = useScenario();

  const context = useContext(
    CommandCenterStateProviderContext
  );

  // -----------------------------------------------------------------------
  // Stable refs to prevent context re-renders from re-triggering effects
  // -----------------------------------------------------------------------

  const contextRef = useRef(context);
  contextRef.current = context;

  const mountedRef = useRef(false);

  const scenarioRef = useRef<any>(scenario);
  scenarioRef.current = scenario;

  const telemetryListeningRef = useRef(isTelemetryListening);

  telemetryListeningRef.current = isTelemetryListening;

  const seriesRef = useRef<TelemetrySeries[]>(telemetrySeries);

  seriesRef.current = telemetrySeries;

  // -----------------------------------------------------------------------
  // WebSocket state
  // -----------------------------------------------------------------------

  const deployDetailsRef = useRef<ScenarioDeploymentDetails | null>(null);

  const socketStateRef = useRef<WebSocketState>({
    socket: null,
    trueTwinId: null,
    generation: 0,
  });

  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // -----------------------------------------------------------------------
  // Telemetry lifecycle
  //
  // Hydration identity is scenario + trueTwinId.
  // This is important because sat_id/trueTwinId can change while the
  // scenario_id remains the same.
  // -----------------------------------------------------------------------

  const activeTrueTwinIdRef = useRef<string | null>(null);

  const hydrationKeyRef = useRef<string | null>(null);

  const fetchingHydrationKeyRef = useRef<string | null>(null);

  const fetchedHydrationKeyRef = useRef<string | null>(null);

  const hydrationGenerationRef = useRef(0);

  // -----------------------------------------------------------------------
  // WS preload buffer
  // -----------------------------------------------------------------------

  const preLoadBufferRef = useRef<Record<string, TelemetrySeries["data"]>>({});

  // -----------------------------------------------------------------------
  // WS batching
  // -----------------------------------------------------------------------

  const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pendingUpdatesRef = useRef<TelemetrySeries[] | null>(null);

  // -----------------------------------------------------------------------
  // Global telemetry store
  // -----------------------------------------------------------------------

  useEffect(() => {
    updateGlobalTelemetrySeriesStore(telemetrySeries);
  }, [telemetrySeries]);

  // =========================================================================
  // WebSocket cleanup helpers
  // =========================================================================

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearPendingBatch = useCallback(() => {
    if (batchTimerRef.current) {
      clearTimeout(batchTimerRef.current);
      batchTimerRef.current = null;
    }

    pendingUpdatesRef.current = null;
  }, []);

  // =========================================================================
  // Disconnect WebSocket
  //
  // This function is called only for intentional lifecycle changes:
  // - page/provider unmount
  // - trueTwinId change
  //
  // Unexpected backend close is handled separately and reconnects.
  // =========================================================================

  const disconnectWebSocket = useCallback(
    (reason: string) => {
      clearReconnectTimer();
      clearPendingBatch();

      const currentState = socketStateRef.current;
      currentState.generation += 1;
      const socket = currentState.socket;
      currentState.socket = null;
      currentState.trueTwinId = null;

      if (!socket) {
        return;
      }
      console.log("WebSocket connection closed by: UI");
      try {
        socket.close(1000, reason.slice(0, 120));
      } catch (error) {
        console.warn("Failed to close telemetry WebSocket:", error);
      }
    },
    [clearReconnectTimer, clearPendingBatch]
  );

  // =========================================================================
  // Connect WebSocket
  // =========================================================================

  const connectWebSocket = useCallback(
    (trueTwinId: string) => {
      if (!trueTwinId || !mountedRef.current) {
        return;
      }

      const currentState = socketStateRef.current;

      // ---------------------------------------------------------------
      // Same trueTwinId already has a healthy/connecting socket.
      // Do NOT disconnect/reconnect.
      // ---------------------------------------------------------------

      if (currentState.socket && currentState.trueTwinId === trueTwinId &&
        (
          currentState.socket.readyState === WebSocket.OPEN ||
          currentState.socket.readyState === WebSocket.CONNECTING
        )
      ) {
        return;
      }

      // ---------------------------------------------------------------
      // If another trueTwinId owns the socket, explicitly close it.
      // ---------------------------------------------------------------

      if (currentState.socket && currentState.trueTwinId !== trueTwinId) {
        try {
          currentState.socket.close(1000, "trueTwinId changed");
        } catch {
          // Ignore close errors.
        }

        currentState.socket = null;
        currentState.trueTwinId = null;
      }

      const baseUrl = getEnvVar("REACT_APP_WEBSOCKET_BACKEND_BASE_URL");
      if (!baseUrl) {
        console.warn("Telemetry WebSocket base URL is not configured.");
        return;
      }

      const token = sessionStorage.getItem("authentication");
      if (!token) {
        console.warn(
          "Telemetry WebSocket authentication token is missing."
        );
        return;
      }

      clearReconnectTimer();

      const sessionId = uuidv4();
      const socketGeneration = currentState.generation + 1;
      currentState.generation = socketGeneration;

      const socket = new WebSocket(`${baseUrl}groundstation/truetwin/telemetryhandler?truetwinId=${trueTwinId}&sessionId=${sessionId}`);

      currentState.socket = socket;
      currentState.trueTwinId = trueTwinId;

      // -----------------------------------------------------------------
      // onopen
      // -----------------------------------------------------------------

      socket.onopen = () => {
        const state = socketStateRef.current;
        // Ignore stale socket events.
        if (state.socket !== socket || state.trueTwinId !== trueTwinId || state.generation !== socketGeneration) {
          return;
        }

        try {
          socket.send(JSON.stringify({ type: "authorization", token, }));
        } catch (error) {
          console.error("Failed to authorize telemetry WebSocket:", error);
        }
      };

      // -----------------------------------------------------------------
      // onmessage
      // -----------------------------------------------------------------

      socket.onmessage = (event) => {
        const state = socketStateRef.current;
        // Ignore messages from stale sockets.
        if (state.socket !== socket || state.trueTwinId !== trueTwinId || state.generation !== socketGeneration) {
          return;
        }

        try {
          const rawData = JSON.parse(event.data);
          const incomingSeriesList = normalizeWsPayload(rawData);

          if (incomingSeriesList.length === 0) {
            return;
          }

          const hydrationKey = hydrationKeyRef.current;
          const hasBaseline = hydrationKey != null && fetchedHydrationKeyRef.current === hydrationKey;

          // -------------------------------------------------------------
          // REST baseline is not ready.
          //
          // Store incoming points so they are merged into the REST
          // response once baseline hydration finishes.
          // -------------------------------------------------------------

          if (!hasBaseline) {
            incomingSeriesList.forEach((incoming) => {
              if (!incoming?.id || !Array.isArray(incoming.data)) {
                return;
              }

              const currentBuffer = preLoadBufferRef.current[incoming.id] ?? [];
              preLoadBufferRef.current[incoming.id] = mergeAndSortTelemetryData(currentBuffer, incoming.data, WS_MERGE_LIMIT);
            }
            );
            return;
          }

          // -------------------------------------------------------------
          // Baseline is ready -> merge live WS data.
          // -------------------------------------------------------------

          const currentSeries = pendingUpdatesRef.current ?? seriesRef.current;

          const { updatedSeriesList, hasUpdates, } = mergeWsIncomingSeries(currentSeries, incomingSeriesList);

          if (!hasUpdates) {
            return;
          }

          pendingUpdatesRef.current = updatedSeriesList;

          // -------------------------------------------------------------
          // Batch high-frequency WS updates.
          // -------------------------------------------------------------

          if (!batchTimerRef.current) {
            batchTimerRef.current = setTimeout(() => {
              batchTimerRef.current = null;
              const pending = pendingUpdatesRef.current;

              if (!pending) {
                return;
              }

              pendingUpdatesRef.current = null;

              // Provider may have unmounted while timer was waiting.
              if (!mountedRef.current) {
                return;
              }

              // Make sure this socket still represents the active
              // trueTwinId before committing the update.
              if (socketStateRef.current.socket !== socket || socketStateRef.current.trueTwinId !== trueTwinId) {
                return;
              }

              setTelemetrySeries(pending);
            }, WS_BATCH_DELAY_MS);
          }
        } catch (error) {
          console.error("Error processing telemetry WebSocket message:", error);
        }
      };

      // -----------------------------------------------------------------
      // onclose
      //
      // Critical:
      // Never blindly set socketRef/current state to null.
      // A stale socket can close after a new socket has already been
      // created.
      // -----------------------------------------------------------------

      socket.onclose = () => {
        const state = socketStateRef.current;
        const isCurrentSocket = state.socket === socket && state.trueTwinId === trueTwinId && state.generation === socketGeneration;

        if (!isCurrentSocket) {
          return;
        }
        console.log("WebSocket connection closed by: Backend");

        state.socket = null;

        // -------------------------------------------------------------
        // If provider is still mounted and this trueTwinId is still
        // active, reconnect instead of leaving telemetry dead.
        // -------------------------------------------------------------

        if (mountedRef.current && activeTrueTwinIdRef.current === trueTwinId) {
          clearReconnectTimer();
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            if (!mountedRef.current || activeTrueTwinIdRef.current !== trueTwinId) {
              return;
            }
            const isEcharts = deployDetailsRef.current?.dashboardVersion === '2.0.0';
            if (!isEcharts) {
              return;
            }
            const status = scenarioRef.current?.status;
            if (isSimulationCompleted(status)) {
              return;
            }

            connectWebSocket(trueTwinId);
          }, WS_RECONNECT_DELAY_MS);

          return;
        }

        state.trueTwinId = null;
      };

      // -----------------------------------------------------------------
      // onerror
      // -----------------------------------------------------------------

      socket.onerror = (error) => {
        const state = socketStateRef.current;
        if (state.socket !== socket || state.trueTwinId !== trueTwinId) {
          return;
        }
        console.error("Telemetry WebSocket error:", error);
      };
    }, [clearReconnectTimer, setTelemetrySeries]);

  // =========================================================================
  // Mounted lifecycle
  // =========================================================================

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      disconnectWebSocket("TelemetryProvider unmounted");
      activeTrueTwinIdRef.current = null;
      deployDetailsRef.current = null;
      hydrationKeyRef.current = null;
      fetchingHydrationKeyRef.current = null;
      fetchedHydrationKeyRef.current = null;

      hydrationGenerationRef.current += 1;

      preLoadBufferRef.current = {};
    };
  }, [disconnectWebSocket]);

  // =========================================================================
  // Telemetry listening state
  //
  // IMPORTANT:
  // We intentionally DO NOT close the WebSocket when listening changes.
  //
  // The requested lifecycle is:
  //   page leave OR trueTwinId change -> disconnect
  //
  // If the existing UI uses isTelemetryListening for some other behavior,
  // that behavior can remain in its consumer without destroying the
  // long-lived WebSocket connection here.
  // =========================================================================

  useEffect(() => {
    telemetryListeningRef.current = isTelemetryListening;
    const trueTwinId = activeTrueTwinIdRef.current;
    if (!trueTwinId || !isTelemetryListening || !mountedRef.current) return;
    const isEcharts = deployDetailsRef.current?.dashboardVersion === '2.0.0';
    if (!isEcharts) return;
    const status = scenarioRef.current?.status;
    if (isSimulationCompleted(status)) return;

    connectWebSocket(trueTwinId);
  }, [
    isTelemetryListening,
    connectWebSocket,
  ]);

  // =========================================================================
  // Main hydration pipeline
  //
  // Hydration identity:
  //   scenario_id + trueTwinId
  //
  // This prevents same-scenario satellite switches from being ignored.
  // =========================================================================

  useEffect(() => {
    if (!mission_id || !scenario_id || !scenario) return;

    let cancelled = false;

    const hydrationGeneration = ++hydrationGenerationRef.current;

    const hydrateTelemetryPipeline = async () => {
      // ---------------------------------------------------------------
      // Stage 0
      // ---------------------------------------------------------------

      let deploymentResult: | ScenarioDeploymentResult | null = null;

      try {
        deploymentResult = await fetchScenarioDeployment(mission_id, scenario_id, sat_id, scenarioRef.current);
      } catch (error) {
        console.error("Failed to resolve telemetry deployment:", error);
      }

      if (cancelled || !mountedRef.current || hydrationGeneration !== hydrationGenerationRef.current) {
        return;
      }

      const activeTrueTwinId = deploymentResult?.activeTrueTwinId ?? null;

      if (!deploymentResult || !activeTrueTwinId) {
        activeTrueTwinIdRef.current = null;
        hydrationKeyRef.current = null;
        disconnectWebSocket("No active trueTwinId");

        setTelemetrySeries([]);
        setTelemetryStatus("not_found");
        setIsTelemetryDataLoading(false);

        return;
      }

      // ---------------------------------------------------------------
      // Build lifecycle identity.
      //
      // Same scenario + same trueTwinId = same hydration lifecycle.
      // Same scenario + different trueTwinId = NEW lifecycle.
      // ---------------------------------------------------------------

      const hydrationKey = `${scenario_id}:${activeTrueTwinId}`;

      const previousTrueTwinId = activeTrueTwinIdRef.current;
      const trueTwinChanged = previousTrueTwinId != null && previousTrueTwinId !== activeTrueTwinId;

      activeTrueTwinIdRef.current = activeTrueTwinId;

      hydrationKeyRef.current = hydrationKey;

      // ---------------------------------------------------------------
      // trueTwinId changed:
      // old WS must be closed and old live-buffer must be discarded.
      // ---------------------------------------------------------------

      if (trueTwinChanged) {
        disconnectWebSocket("trueTwinId changed");
        preLoadBufferRef.current = {};
        pendingUpdatesRef.current = null;
        setTelemetrySeries([]);
      }

      // ---------------------------------------------------------------
      // Already hydrated for exactly this scenario + trueTwinId.
      //
      // Keep existing WS alive.
      // ---------------------------------------------------------------

      if (fetchedHydrationKeyRef.current === hydrationKey || fetchingHydrationKeyRef.current === hydrationKey) {
        const status = deploymentResult.deployDetails?.status ?? scenarioRef.current?.status;
        const isEcharts = deploymentResult.deployDetails?.dashboardVersion === '2.0.0';

        if (!isSimulationCompleted(status) && isEcharts) {
          connectWebSocket(activeTrueTwinId);
        }
        return;
      }

      fetchingHydrationKeyRef.current = hydrationKey;

      setTelemetryStatus("loading");
      setIsTelemetryDataLoading(true);

      try {
        // -------------------------------------------------------------
        // Save deployment details
        // -------------------------------------------------------------

        if (deploymentResult.deployDetails) {
          deployDetailsRef.current = deploymentResult.deployDetails;
          setScenarioDeploymentDetails(deploymentResult.deployDetails);
        }

        const isEcharts = deploymentResult.deployDetails?.dashboardVersion === '2.0.0';

        // If dashboardVersion is NOT '2.0.0' (e.g. 'GRAFANA' or undefined),
        // skip telemetry WebSocket and REST API calls.
        if (!isEcharts) {
          disconnectWebSocket("dashboardVersion is not 2.0.0");
          preLoadBufferRef.current = {};
          pendingUpdatesRef.current = null;
          setTelemetrySeries([]);
          setTelemetryStatus("idle");
          setIsTelemetryDataLoading(false);
          fetchedHydrationKeyRef.current = hydrationKey;
          return;
        }

        const deploymentStatus = deploymentResult.deployDetails?.status ?? scenarioRef.current?.status;
        const completed = isSimulationCompleted(deploymentStatus);

        // -------------------------------------------------------------
        // Connect WS BEFORE REST baseline.
        //
        // Incoming data will be buffered while REST is loading.
        // -------------------------------------------------------------

        if (!completed && telemetryListeningRef.current) {
          connectWebSocket(activeTrueTwinId);
        }

        // -------------------------------------------------------------
        // Stage 1
        // -------------------------------------------------------------

        const dashboardResult = await fetchDashboardAndCatalog(activeTrueTwinId, scenarioRef.current?.scenarioDeploymentType);

        if (cancelled || !mountedRef.current || hydrationGeneration !== hydrationGenerationRef.current || activeTrueTwinIdRef.current !== activeTrueTwinId) {
          return;
        }

        if (!dashboardResult.isDashOk) {
          setTelemetryDashboards(dashboardResult.emptyDashboard);
          setTelemetrySeries([]);
          setTelemetryStatus("not_found");
          fetchedHydrationKeyRef.current = hydrationKey;

          return;
        }

        setTelemetryDashboards(dashboardResult.dashboardConfig);
        contextRef.current?.setSavedTelemetryDashboards?.(dashboardResult.savedDashboardConfig);

        setHasUnsavedChanges(dashboardResult.hasUnsavedChanges);

        if (dashboardResult.initialActiveWorkspaceId) {
          setActiveTelemetryDashboardId(dashboardResult.initialActiveWorkspaceId);
        }

        if (deploymentResult.initialTimeframeMinutes) {
          contextRef.current?.setTelemetryTimeframe?.(deploymentResult.initialTimeframeMinutes);

          contextRef.current?.setTelemetryViewport?.({
            spanMs: deploymentResult.initialTimeframeMinutes * 60_000,
            offsetMs: 0,
          });
        }

        // Dashboard is ready.
        setTelemetryStatus("success");

        // -------------------------------------------------------------
        // Stage 2
        // -------------------------------------------------------------

        const rawBaseline = await fetchBaselineTelemetryData(activeTrueTwinId, deploymentResult.telemetryFetchParams);

        // -------------------------------------------------------------
        // Ignore stale REST result.
        // -------------------------------------------------------------

        if (
          cancelled ||
          !mountedRef.current ||
          hydrationGeneration !==
          hydrationGenerationRef.current ||
          activeTrueTwinIdRef.current !==
          activeTrueTwinId ||
          hydrationKeyRef.current !==
          hydrationKey
        ) {
          return;
        }

        // Merge buffered WS points that arrived during Stage 0, 1 & 2
        const currentBuffer = preLoadBufferRef.current;
        const mergedBaseline = rawBaseline.map((item) => {
          const bufferedPoints = currentBuffer[item.id] ?? [];
          return {
            ...item,
            data: mergeAndSortTelemetryData(
              item.data ?? [],
              bufferedPoints,
              WS_MERGE_LIMIT
            ),
          };
        });

        setTelemetrySeries(mergedBaseline);

        fetchedHydrationKeyRef.current = hydrationKey;

        preLoadBufferRef.current = {};
      } catch (error) {
        if (
          cancelled ||
          !mountedRef.current ||
          hydrationGeneration !==
          hydrationGenerationRef.current ||
          activeTrueTwinIdRef.current !==
          activeTrueTwinId
        ) {
          return;
        }

        console.error("Telemetry hydration pipeline encountered an error:", error);

        setTelemetryDashboards({
          uniqueId: "config",
          truetwinId:
            activeTrueTwinId,
          dashboardType: "SIMULATE",
          workspaces: [],
        });

        setTelemetrySeries([]);
        setTelemetryStatus("not_found");
        fetchedHydrationKeyRef.current = hydrationKey;
      } finally {
        if (fetchingHydrationKeyRef.current === hydrationKey) {
          fetchingHydrationKeyRef.current = null;
        }

        if (hydrationGeneration === hydrationGenerationRef.current && activeTrueTwinIdRef.current === activeTrueTwinId) {
          setIsTelemetryDataLoading(false);
        }
      }
    };

    hydrateTelemetryPipeline();

    return () => {
      cancelled = true;

      /*
       * IMPORTANT:
       *
       * Do NOT disconnect WebSocket here.
       *
       * React may re-run this effect because dependencies such as
       * scenario/sat_id changed while the actual trueTwinId remained
       * the same.
       *
       * WebSocket lifecycle is controlled explicitly by trueTwinId
       * changes and provider unmount instead.
       */
    };
  }, [
    mission_id,
    scenario_id,
    sat_id,
    scenario,

    disconnectWebSocket,
    connectWebSocket,

    setTelemetrySeries,
    setTelemetryStatus,
    setTelemetryDashboards,
    setActiveTelemetryDashboardId,
    setHasUnsavedChanges,
    setIsTelemetryDataLoading,
    setScenarioDeploymentDetails,
  ]);

  return children;
};

export default TelemetryProvider;
